import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Gift, Save, Plus, Trash2, Coins, Users, RefreshCw, Check, X } from "lucide-react";

const REWARD_TYPES = [
  { value: "discount_percent", label: "Prozent-Rabatt (%)" },
  { value: "discount_fixed", label: "Fester Rabatt (€)" },
  { value: "free_service", label: "Gratis-Leistung" },
];

const TABS = [
  { key: "settings", label: "Einstellungen" },
  { key: "rewards", label: "Belohnungen" },
  { key: "customers", label: "Kunden" },
  { key: "transactions", label: "Transaktionen" },
  { key: "redemptions", label: "Einlösungen" },
];

const EMPTY_REWARD = {
  name: "",
  description: "",
  points_cost: 100,
  reward_type: "discount_percent",
  reward_value: 10,
  service_id: "",
  active: true,
};

export default function LoyaltyManager() {
  const [tab, setTab] = useState("settings");
  const [settings, setSettings] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null);
  const [savedMsg, setSavedMsg] = useState("");
  const [adjusting, setAdjusting] = useState(null); // { customer, amount, reason }

  const load = async () => {
    const [s, r, c, t, rd, sv] = await Promise.all([
      api.get("/cms/loyalty/settings"),
      api.get("/cms/loyalty/rewards"),
      api.get("/cms/loyalty/customers"),
      api.get("/cms/loyalty/transactions"),
      api.get("/cms/loyalty/redemptions"),
      api.get("/cms/services"),
    ]);
    setSettings(s.data);
    setRewards(r.data);
    setCustomers(c.data);
    setTransactions(t.data);
    setRedemptions(rd.data);
    setServices(sv.data);
  };

  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    const payload = {
      enabled: !!settings.enabled,
      points_per_euro: parseFloat(settings.points_per_euro) || 0,
      expiry_months: parseInt(settings.expiry_months) || 0,
      min_booking_amount: parseFloat(settings.min_booking_amount) || 0,
      welcome_bonus: parseInt(settings.welcome_bonus) || 0,
      birthday_bonus: parseInt(settings.birthday_bonus) || 0,
      expiry_warning_days: parseInt(settings.expiry_warning_days) || 0,
    };
    const { data } = await api.put("/cms/loyalty/settings", payload);
    setSettings(data);
    setSavedMsg("Gespeichert");
    setTimeout(() => setSavedMsg(""), 2000);
  };

  const saveReward = async () => {
    const payload = {
      name: editing.name,
      description: editing.description || "",
      points_cost: parseInt(editing.points_cost) || 0,
      reward_type: editing.reward_type || "discount_percent",
      reward_value: parseFloat(editing.reward_value) || 0,
      service_id: editing.reward_type === "free_service" ? (editing.service_id || null) : null,
      active: !!editing.active,
    };
    if (editing.id) {
      await api.put(`/cms/loyalty/rewards/${editing.id}`, payload);
    } else {
      await api.post("/cms/loyalty/rewards", payload);
    }
    setEditing(null);
    load();
  };

  const removeReward = async (id) => {
    if (!window.confirm("Belohnung löschen?")) return;
    await api.delete(`/cms/loyalty/rewards/${id}`);
    load();
  };

  const applyAdjust = async () => {
    if (!adjusting?.customer || !adjusting.amount) return;
    await api.post("/cms/loyalty/adjust", {
      customer_id: adjusting.customer.id,
      amount: parseInt(adjusting.amount) || 0,
      reason: adjusting.reason || "Manuelle Anpassung",
    });
    setAdjusting(null);
    load();
  };

  const markUsed = async (rid) => {
    await api.put(`/cms/loyalty/redemptions/${rid}/mark-used`);
    load();
  };

  if (!settings) return <div className="p-12">Lade ...</div>;

  return (
    <div className="p-8 lg:p-12 mv-fade-in">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="mv-label mb-2">Kundenbindung</p>
          <h1 className="font-display text-4xl flex items-center gap-3">
            <Gift className="w-8 h-8" strokeWidth={1.5} style={{ color: "var(--mv-primary)" }} />
            Treueprogramm
          </h1>
        </div>
        <button data-testid="loyalty-refresh" onClick={load} className="mv-btn-ghost">
          <RefreshCw className="w-4 h-4" /> Aktualisieren
        </button>
      </div>

      <div className="flex border-b mb-6 overflow-x-auto" style={{ borderColor: "var(--mv-border)" }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`loyalty-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className="px-5 py-3 text-sm border-b-2 transition-colors whitespace-nowrap"
            style={{
              borderColor: tab === t.key ? "var(--mv-primary)" : "transparent",
              color: tab === t.key ? "var(--mv-primary)" : "var(--mv-text-muted)",
              fontWeight: tab === t.key ? 600 : 400,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "settings" && (
        <div className="mv-card p-6 max-w-2xl space-y-5">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!settings.enabled}
              onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
              data-testid="loyalty-enabled"
            />
            Treueprogramm aktiviert
          </label>
          <div>
            <label className="block text-xs mb-1">Punkte pro Euro Umsatz</label>
            <input
              type="number"
              step="0.1"
              className="mv-input"
              value={settings.points_per_euro}
              onChange={(e) => setSettings({ ...settings, points_per_euro: e.target.value })}
              data-testid="loyalty-points-per-euro"
            />
            <p className="text-xs mt-1" style={{ color: "var(--mv-text-muted)" }}>
              Beispiel: 1 = 1 Punkt pro €. Bei 0,5 erhalten Kunden halbe Punkte pro €.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs mb-1">Mindest-Buchungswert (€)</label>
              <input
                type="number"
                step="0.01"
                className="mv-input"
                value={settings.min_booking_amount}
                onChange={(e) => setSettings({ ...settings, min_booking_amount: e.target.value })}
                data-testid="loyalty-min-booking"
              />
              <p className="text-xs mt-1" style={{ color: "var(--mv-text-muted)" }}>0 = kein Mindestwert</p>
            </div>
            <div>
              <label className="block text-xs mb-1">Willkommens-Bonus (Punkte)</label>
              <input
                type="number"
                className="mv-input"
                value={settings.welcome_bonus}
                onChange={(e) => setSettings({ ...settings, welcome_bonus: e.target.value })}
                data-testid="loyalty-welcome-bonus"
              />
              <p className="text-xs mt-1" style={{ color: "var(--mv-text-muted)" }}>Bei Registrierung gutgeschrieben</p>
            </div>
          </div>
          <div>
            <label className="block text-xs mb-1">Punkte verfallen nach (Monaten)</label>
            <input
              type="number"
              className="mv-input"
              value={settings.expiry_months}
              onChange={(e) => setSettings({ ...settings, expiry_months: e.target.value })}
              data-testid="loyalty-expiry-months"
            />
            <p className="text-xs mt-1" style={{ color: "var(--mv-text-muted)" }}>0 = niemals verfallen</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs mb-1">Geburtstags-Bonus (Punkte)</label>
              <input
                type="number"
                className="mv-input"
                value={settings.birthday_bonus || 0}
                onChange={(e) => setSettings({ ...settings, birthday_bonus: e.target.value })}
                data-testid="loyalty-birthday-bonus"
              />
              <p className="text-xs mt-1" style={{ color: "var(--mv-text-muted)" }}>Einmal pro Jahr am Geburtstag</p>
            </div>
            <div>
              <label className="block text-xs mb-1">E-Mail vor Verfall (Tage vorher)</label>
              <input
                type="number"
                className="mv-input"
                value={settings.expiry_warning_days || 14}
                onChange={(e) => setSettings({ ...settings, expiry_warning_days: e.target.value })}
                data-testid="loyalty-expiry-warning-days"
              />
              <p className="text-xs mt-1" style={{ color: "var(--mv-text-muted)" }}>0 = keine Vorwarnung</p>
            </div>
          </div>
          <div className="flex items-center gap-3 pt-2">
            <button data-testid="loyalty-save-settings" onClick={saveSettings} className="mv-btn">
              <Save className="w-4 h-4" /> Speichern
            </button>
            {savedMsg && <p className="text-sm" style={{ color: "var(--mv-primary)" }}>{savedMsg}</p>}
          </div>
        </div>
      )}

      {tab === "rewards" && (
        <div>
          <div className="flex justify-end mb-4">
            <button data-testid="loyalty-new-reward" onClick={() => setEditing({ ...EMPTY_REWARD })} className="mv-btn">
              <Plus className="w-4 h-4" /> Neue Belohnung
            </button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {rewards.length === 0 && (
              <div className="mv-card p-6 col-span-full text-center text-sm" style={{ color: "var(--mv-text-muted)" }}>
                Noch keine Belohnungen angelegt.
              </div>
            )}
            {rewards.map((r) => (
              <div key={r.id} className="mv-card p-5" data-testid={`reward-card-${r.id}`}>
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="font-display text-lg">{r.name}</h3>
                    <p className="text-xs" style={{ color: "var(--mv-text-muted)" }}>
                      {REWARD_TYPES.find((t) => t.value === r.reward_type)?.label || r.reward_type}
                      {r.reward_type === "discount_percent" && ` · ${r.reward_value}%`}
                      {r.reward_type === "discount_fixed" && ` · ${r.reward_value} €`}
                    </p>
                  </div>
                  <span className="text-xs px-2.5 py-1 rounded-full" style={{ background: "var(--mv-primary)22", color: "var(--mv-primary)" }}>
                    <Coins className="inline w-3 h-3 mr-1" /> {r.points_cost}
                  </span>
                </div>
                {r.description && <p className="text-xs opacity-75 mb-3">{r.description}</p>}
                <div className="flex gap-2">
                  <button onClick={() => setEditing(r)} className="mv-btn-ghost text-xs">Bearbeiten</button>
                  <button onClick={() => removeReward(r.id)} className="text-xs text-red-500 px-2">Löschen</button>
                  <span className="ml-auto text-xs" style={{ color: r.active ? "var(--mv-secondary)" : "var(--mv-text-muted)" }}>
                    {r.active ? "● Aktiv" : "○ Inaktiv"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "customers" && (
        <div className="mv-card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="text-left text-xs" style={{ color: "var(--mv-text-muted)" }}>
                <th className="p-3">Name</th>
                <th className="p-3">E-Mail</th>
                <th className="p-3 text-right">Punktestand</th>
                <th className="p-3 text-right">Letzte Aktivität</th>
                <th className="p-3 text-right">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {customers.length === 0 && (
                <tr><td colSpan="5" className="p-6 text-center text-sm opacity-70">Noch keine Kunden registriert.</td></tr>
              )}
              {customers.map((c) => (
                <tr key={c.id} className="border-t" style={{ borderColor: "var(--mv-border)" }} data-testid={`loyalty-customer-${c.id}`}>
                  <td className="p-3 text-sm">{c.name}</td>
                  <td className="p-3 text-sm">{c.email}</td>
                  <td className="p-3 text-sm text-right font-medium">{c.points_balance || 0}</td>
                  <td className="p-3 text-xs opacity-70 text-right">{c.points_last_activity ? new Date(c.points_last_activity).toLocaleDateString("de-DE") : "—"}</td>
                  <td className="p-3 text-right">
                    <button onClick={() => setAdjusting({ customer: c, amount: 0, reason: "" })} className="mv-btn-ghost text-xs">
                      <Coins className="w-3 h-3" /> Anpassen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "transactions" && (
        <div className="mv-card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="text-left text-xs" style={{ color: "var(--mv-text-muted)" }}>
                <th className="p-3">Datum</th>
                <th className="p-3">Kunde</th>
                <th className="p-3">Grund</th>
                <th className="p-3 text-right">Punkte</th>
                <th className="p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {transactions.length === 0 && (
                <tr><td colSpan="5" className="p-6 text-center text-sm opacity-70">Noch keine Transaktionen.</td></tr>
              )}
              {transactions.map((t) => {
                const cust = customers.find((c) => c.id === t.customer_id);
                return (
                  <tr key={t.id} className="border-t" style={{ borderColor: "var(--mv-border)" }}>
                    <td className="p-3 text-xs">{new Date(t.created_at).toLocaleString("de-DE")}</td>
                    <td className="p-3 text-sm">{cust ? cust.name : "—"}</td>
                    <td className="p-3 text-xs opacity-80">{t.reason}</td>
                    <td className="p-3 text-sm text-right font-medium" style={{ color: t.amount >= 0 ? "var(--mv-secondary)" : "#b91c1c" }}>
                      {t.amount > 0 ? "+" : ""}{t.amount}
                    </td>
                    <td className="p-3 text-xs">{t.status === "expired" ? "Verfallen" : "Aktiv"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {tab === "redemptions" && (
        <div className="mv-card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="text-left text-xs" style={{ color: "var(--mv-text-muted)" }}>
                <th className="p-3">Datum</th>
                <th className="p-3">Kunde</th>
                <th className="p-3">Belohnung</th>
                <th className="p-3">Code</th>
                <th className="p-3">Status</th>
                <th className="p-3 text-right">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {redemptions.length === 0 && (
                <tr><td colSpan="6" className="p-6 text-center text-sm opacity-70">Noch keine Einlösungen.</td></tr>
              )}
              {redemptions.map((r) => (
                <tr key={r.id} className="border-t" style={{ borderColor: "var(--mv-border)" }} data-testid={`redemption-${r.id}`}>
                  <td className="p-3 text-xs">{new Date(r.created_at).toLocaleString("de-DE")}</td>
                  <td className="p-3 text-sm">{r.customer?.name || "—"}</td>
                  <td className="p-3 text-sm">{r.reward_name}</td>
                  <td className="p-3 text-xs font-mono">{r.code}</td>
                  <td className="p-3 text-xs">
                    {r.status === "used" ? (
                      <span style={{ color: "var(--mv-secondary)" }}><Check className="inline w-3 h-3" /> Eingelöst</span>
                    ) : (
                      <span style={{ color: "var(--mv-primary)" }}>Offen</span>
                    )}
                  </td>
                  <td className="p-3 text-right">
                    {r.status !== "used" && (
                      <button onClick={() => markUsed(r.id)} className="mv-btn-ghost text-xs" data-testid={`mark-used-${r.id}`}>
                        <Check className="w-3 h-3" /> Als verwendet
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Reward editor modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.4)" }} onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-display text-2xl mb-4">{editing.id ? "Belohnung bearbeiten" : "Neue Belohnung"}</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-xs mb-1">Name</label>
                <input className="mv-input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="reward-edit-name" />
              </div>
              <div>
                <label className="block text-xs mb-1">Beschreibung</label>
                <textarea className="mv-input min-h-[60px]" value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs mb-1">Punktekosten</label>
                  <input type="number" className="mv-input" value={editing.points_cost} onChange={(e) => setEditing({ ...editing, points_cost: e.target.value })} data-testid="reward-edit-cost" />
                </div>
                <div>
                  <label className="block text-xs mb-1">Typ</label>
                  <select className="mv-input" value={editing.reward_type} onChange={(e) => setEditing({ ...editing, reward_type: e.target.value })} data-testid="reward-edit-type">
                    {REWARD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>
              {editing.reward_type !== "free_service" && (
                <div>
                  <label className="block text-xs mb-1">
                    Wert {editing.reward_type === "discount_percent" ? "(%)" : "(€)"}
                  </label>
                  <input type="number" step="0.01" className="mv-input" value={editing.reward_value} onChange={(e) => setEditing({ ...editing, reward_value: e.target.value })} data-testid="reward-edit-value" />
                </div>
              )}
              {editing.reward_type === "free_service" && (
                <div>
                  <label className="block text-xs mb-1">Gratis-Leistung</label>
                  <select className="mv-input" value={editing.service_id || ""} onChange={(e) => setEditing({ ...editing, service_id: e.target.value })}>
                    <option value="">— Beliebige Leistung (Preis des gewählten Services) —</option>
                    {services.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.price?.toFixed(2)} €)</option>)}
                  </select>
                </div>
              )}
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} />
                Aktiv (sichtbar für Kunden)
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setEditing(null)} className="mv-btn-ghost">Abbrechen</button>
              <button onClick={saveReward} className="mv-btn" data-testid="reward-save"><Save className="w-4 h-4" /> Speichern</button>
            </div>
          </div>
        </div>
      )}

      {/* Adjust modal */}
      {adjusting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.4)" }} onClick={() => setAdjusting(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-display text-2xl mb-1">Punkte anpassen</h2>
            <p className="text-sm opacity-70 mb-4">{adjusting.customer.name} · {adjusting.customer.email} · Aktuell: {adjusting.customer.points_balance || 0}</p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs mb-1">Punkte (negativ zum Abziehen)</label>
                <input type="number" className="mv-input" value={adjusting.amount} onChange={(e) => setAdjusting({ ...adjusting, amount: e.target.value })} data-testid="adjust-amount" />
              </div>
              <div>
                <label className="block text-xs mb-1">Grund</label>
                <input className="mv-input" value={adjusting.reason} onChange={(e) => setAdjusting({ ...adjusting, reason: e.target.value })} placeholder="z.B. Kulanz, Korrektur" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setAdjusting(null)} className="mv-btn-ghost">Abbrechen</button>
              <button onClick={applyAdjust} className="mv-btn" data-testid="adjust-save"><Save className="w-4 h-4" /> Anpassen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
